

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[] | any;
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const cryolipolysis: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "cryolipolysis",
        "meta": {
            "title": "Cryolipolysis (Fat Freezing) in Dubai | Elite Body Home",
            "description": "Non-invasive fat reduction with cryolipolysis (fat freezing) to target and destroy unwanted fat cells for a more sculpted look. Safe, effective treatment with no downtime.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/cryolipolysis",
            "og": {
                "title": "Cryolipolysis (Fat Freezing) in Dubai | Elite Body Home",
                "description": "Non-invasive fat reduction with cryolipolysis in Dubai",
                "image": "https://www.elitebodyhome.com/images/cryolipolysis-og.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Cryolipolysis (Fat Freezing)"],
        "hero": {
            "title": "Cryolipolysis (Fat Freezing) in Dubai",
            "subtitle": "Non-Invasive Fat Reduction",
            "description": "Cryolipolysis, popularly known as 'fat freezing,' is a non-invasive cosmetic procedure that targets and destroys unwanted fat. This body-contouring procedure has become extremely well-liked thanks to its non-invasive method of fat reduction that aids in getting a more sculpted look."
        },
        "howItWorks": {
            "title": "What is Cryolipolysis?",
            "description": "Cryolipolysis is an advanced fat reduction procedure that utilizes the strategy of freezing fat cells. It works on the science of cryolipolysis, which states that fat cells are more susceptible to cold temperatures than other tissues. It crystalizes, destroys and then naturally flushes out over time the cells from targeted fat areas by freezing them to certain temperature. The most popular device for this process is CoolSculpting®, however, many other technologies exist today."
        },
        "procedureSteps": {
            "heading": "Procedure Steps For Cryolipolysis",
            "para": "The non-invasive process for Cryolipolysis, or 'fat freezing,' typically follows these steps:",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "A detailed consultation is performed to evaluate the areas in the body you target, skin type, and apply if you qualify for the treatments.",
                    "icon": "fa-solid fa-clipboard-question"
                },
                {
                    "title": "Preparation",
                    "desc": "A circle will demarcate the area to be treated and the gel pad to protect the skin from the cold during the treatment will be placed.",
                    "icon": "fa-solid fa-circle"
                },
                {
                    "title": "Application",
                    "desc": "The Cryolipolysis device is positioned on the targeted area, and precise cooling is administered to freeze and break down fat cells below the skin. You'll experience a cool feeling and slight suction as the device attaches to your skin.",
                    "icon": "fa-solid fa-snowflake"
                },
                {
                    "title": "Post-Treatment Massage",
                    "desc": "After removing the device, a gentle massage is performed to further aid in the breakdown of the fat cells and assist blood circulation in the treatment area.",
                    "icon": "fa-solid fa-hands"
                },
                {
                    "title": "Treatment Duration",
                    "desc": "The session takes approximately 35-60 minutes depending on the area being treated. For this duration you can unwind, read or even take a nap.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "Recovery",
                    "desc": "It does not require any downtime and you can immediately return to your normal activities. Mild redness, swelling or numbness in the treated area may occur, but these effects usually resolve within a few days.",
                    "icon": "fa-solid fa-heart-pulse"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "Cryolipolysis has numerous benefits for anyone looking for a non-invasive fat reduction treatment",
            "para": "When it comes to body sculpting, these benefits explain why it is among the first options to consider.",
            "items": [
                {
                    "title": "Non-Invasive",
                    "desc": "No risks of surgery and no needles, no anesthesia, no downtime.",
                    "icon": "fa-solid fa-circle-check"
                },
                {
                    "title": "Targeted Fat Loss",
                    "desc": "Significantly removes fat from diet and exercise resistant areas.",
                    "icon": "fa-solid fa-bullseye"
                },
                {
                    "title": "Long-Lasting Results",
                    "desc": "The fat cells that are destroyed using this method are gone forever.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Quick Recovery",
                    "desc": "Patients can leave for their routine tasks straight after the session, so very suitable for the busy schedule.",
                    "icon": "fa-solid fa-business-time"
                },
                {
                    "title": "Minimal Side Effects",
                    "desc": "Mild redness, swelling or numbness, which usually go away within a few days, are common side effects.",
                    "icon": "fa-solid fa-bandaid"
                },
                {
                    "title": "Versatile Treatment Areas",
                    "desc": "Reaches various regions, including belly, flanks, thighs, arms, and even the double chin.",
                    "icon": "fa-solid fa-map-location-dot"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "Who is A Suitable Candidate for Cryolipolysis?",
            "para": "Cryolipolysis is best suited for people who are near their body weight goal but have stubborn fat in some areas. But it is not a weight-loss cure. But knowing who is the ideal candidate for this treatment is the key to effective results.",
            "items": [
                {
                    "title": "Healthy Individuals",
                    "desc": "Candidates must be in good general health and have no serious medical illnesses.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "Localized Fat Deposits",
                    "desc": "Those who have pinchable fat in certain areas like the abdomen, flanks, arms or thighs.",
                    "icon": "fa-solid fa-hand-holding-hand"
                },
                {
                    "title": "Near Goal Weight",
                    "desc": "Most Ideal For: Men and women who cannot successfully eliminate stubborn fat pockets through diet and exercise.",
                    "icon": "fa-solid fa-weight-scale"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "How to Prepare for Cryolipolysis",
            "para": "While preparation for Cryolipolysis is simple, there are a few steps you can take to ensure your treatment goes smoothly and effectively.",
            "items": [
                {
                    "title": "Consultation",
                    "desc": "Book a consultation with a qualified practitioner to determine if it is right for you and discuss goals.",
                    "icon": "fa-solid fa-clipboard-question"
                },
                {
                    "title": "Hydration",
                    "desc": "Do drink enough water before and after the session as it helps eliminate the fat.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Comfortable Clothing",
                    "desc": "Wear loose, comfortable clothing to the appointment (especially around the area you're treating).",
                    "icon": "fa-solid fa-shirt"
                },
                {
                    "title": "Avoid Anti-Inflammatories",
                    "desc": "For a week before the treatment, do not take anti-inflammatory medications, such as ibuprofen, to lessen bruising.",
                    "icon": "fa-solid fa-pills"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "What to Expect After Cryolipolysis",
            "para": "The goal of Cryolipolysis is to achieve gradual fat loss over time, and the results are permanent as long as a healthy diet and lifestyle is followed. Knowing the time frame for results can help set expectations.",
            "items": [
                {
                    "title": "Visible Results",
                    "desc": "Results might show up after 3 weeks or so after treatment, however most are typically 2-3 months the best.",
                    "icon": "fa-solid fa-eye"
                },
                {
                    "title": "Continued Improvement",
                    "desc": "Fat cells may continue to be shed in response to the treatment for up to 6 months after the procedure.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Long-Lasting Results",
                    "desc": "Fat cells are irreversibly destroyed, but a steady diet and exercise are essential for keeping the newly sculpted figure.",
                    "icon": "fa-solid fa-infinity"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare Tips",
            "subheading": "Maintaining Your Results",
            "para": "To maximize and maintain your cryolipolysis results, follow these recommendations:",
            "items": [
                {
                    "title": "Stay Hydrated",
                    "desc": "Continue drinking plenty of water to help flush out the destroyed fat cells.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Maintain Healthy Diet",
                    "desc": "Follow a balanced diet to prevent new fat accumulation in treated areas.",
                    "icon": "fa-solid fa-apple-whole"
                },
                {
                    "title": "Regular Exercise",
                    "desc": "Combine your treatment with regular physical activity to enhance results.",
                    "icon": "fa-solid fa-person-walking"
                },
                {
                    "title": "Follow-up Sessions",
                    "desc": "Consider additional sessions if needed for optimal results.",
                    "icon": "fa-solid fa-calendar-days"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "Common questions about cryolipolysis",
            "items": [
                {
                    "question": "How many sessions will I need?",
                    "answer": "Most patients achieve desired results with 1-2 sessions per treatment area, spaced a few months apart. Your practitioner will recommend the optimal number during your consultation."
                },
                {
                    "question": "Is the treatment painful?",
                    "answer": "You may feel intense cold and pulling sensation initially, but this typically subsides as the area becomes numb. Most patients read or relax during the procedure."
                },
                {
                    "question": "How soon will I see results?",
                    "answer": "You may begin seeing results within three weeks post-treatment, with full results seen after two to three months as your body naturally eliminates the fat cells."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "Common temporary side effects include redness, swelling, bruising, numbness or tingling in the treated area, which typically resolve within days or weeks."
                },
                {
                    "question": "Is the fat reduction permanent?",
                    "answer": "Yes, the destroyed fat cells are permanently eliminated. However, maintaining a stable weight through diet and exercise is important to prevent remaining fat cells from enlarging."
                },
                {
                    "question": "Who is not a good candidate?",
                    "answer": "Cryolipolysis is not for obese individuals or those with cryoglobulinemia, cold agglutinin disease, or paroxysmal cold hemoglobinuria."
                }
            ]
        },
        "treatmentAreas1": [
            {
                "ar": "البطن",
                "title": "Abdomen",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/abdomen-cryo.jpg"
            },
            {
                "ar": "الجوانب",
                "title": "Flanks",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/flanks-cryo.jpg"
            },
            {
                "ar": "الفخذين",
                "title": "Thighs",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/thighs-cryo.jpg"
            }
        ],
        "treatmentAreas2": [
            {
                "ar": "الذراعين",
                "title": "Arms",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/arms-cryo.jpg"
            },
            {
                "ar": "الظهر",
                "title": "Back",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/back-cryo.jpg"
            },
            {
                "ar": "الذقن المزدوج",
                "title": "Double Chin",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/chin-cryo.jpg"
            }
        ]
    },
    "ar": {
        "url": "cryolipolysis",
        "meta": {
            "title": "تحلل الدهون بالتبريد (الكرايو ليبوليسيس) في دبي | ايليت بودي هوم",
            "description": "إجراء غير جراحي لتقليل الدهون باستخدام تقنية الكرايو ليبوليسيس (تجميد الدهون) لاستهداف وتدمير الخلايا الدهنية غير المرغوب فيها للحصول على مظهر أكثر نحتاً. علاج آمن وفعال دون تعافي.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/cryolipolysis",
            "og": {
                "title": "تحلل الدهون بالتبريد (الكرايو ليبوليسيس) في دبي | ايليت بودي هوم",
                "description": "إجراء غير جراحي لتقليل الدهون باستخدام تقنية الكرايو ليبوليسيس في دبي",
                "image": "https://www.elitebodyhome.com/images/cryolipolysis-og.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "تحلل الدهون بالتبريد (الكرايو ليبوليسيس)"],
        "hero": {
            "title": "تحلل الدهون بالتبريد (الكرايو ليبوليسيس) في دبي",
            "subtitle": "تقليل الدهون غير الجراحي",
            "description": "يُعرف تحلل الدهون بالتبريد بتجميد الدهون، وهو إجراء تجميلي غير جراحي يهدف إلى استهداف الدهون غير المرغوب فيها وتدميرها. وقد أصبحت هذه التقنية الخاصة بالنحت البارد للجسم تحظى بمحبة كبيرة بفضل طريقتها الآمنة وغير الجراحية في تقليل الدهون، مما يساعد على تحقيق مظهر أكثر تحديدًا."
        },
        "howItWorks": {
            "title": "ما هو تحلل الدهون بالتبريد؟",
            "description": "الكرايو ليبوليسيس هو إجراء متطور لتقليل الدهون يعتمد على تقنية تجميد الخلايا الدهنية، ويعتمد على مبدأ علمي يقر بإن الخلايا الدهنية تكون أكثر حساسية لدرجات الحرارة الباردة مقارنةً بالأنسجة الأخرى. يتم تجميد الخلايا الدهنية حتى تصل إلى درجة حرارة معينة، مما يؤدي إلى تبلورها وتدميرها، ثم يتخلص الجسم منها بشكل طبيعي بمرور الوقت. ويُعد جهاز كول سكلبتنج من أشهر الأجهزة المستخدمة في هذا الإجراء، مع وجود العديد من التقنيات الأخرى المتوفرة حاليًا."
        },
        "procedureSteps": {
            "heading": "خطوات إجراء الكرايو ليبوليسيس",
            "para": "يتبع إجراء الكرايو ليبوليسيس أو تحلل الدهون بالتبريد غير الجراحي عادةً الخطوات التالية:",
            "steps": [
                {
                    "title": "استشارة الطبيب",
                    "desc": "تُجرى استشارة مفصلة لتقييم المناطق المستهدفة في الجسم ونوع البشرة والتأكد من ملاءمتك للعلاج.",
                    "icon": "fa-solid fa-clipboard-question"
                },
                {
                    "title": "الاستعداد",
                    "desc": "تُحدد المنطقة التي ستخضع للعلاج بدائرة واضحة، ثم توضع ضمادة هلامية لحماية الجلد من تأثير البرودة أثناء جلسة تجميد الدهون.",
                    "icon": "fa-solid fa-circle"
                },
                {
                    "title": "استخدام جهاز التبريد",
                    "desc": "يُوضع جهاز الكرايو ليبوليسيس على المنطقة المستهدفة، ويُوجه تبريد دقيق لتجميد وتحطيم الخلايا الدهنية تحت سطح الجلد. ستشعر بالبرودة مع شفط خفيف نتيجة التصاق الجهاز بالجلد.",
                    "icon": "fa-solid fa-snowflake"
                },
                {
                    "title": "تدليك ما بعد العلاج",
                    "desc": "يُجرى تدليك لطيف على المنطقة المعالجة بعد إزالة الجهاز، للمساعدة في تفتيت الخلايا الدهنية وتنشيط الدورة الدموية في المنطقة المعالجة بشكل أكبر.",
                    "icon": "fa-solid fa-hands"
                },
                {
                    "title": "مدة الجلسة",
                    "desc": "تستغرق الجلسة من 35 إلى 60 دقيقة، وذلك حسب المنطقة التي يتم علاجها. يمكنك الاسترخاء خلال هذه الفترة أو القراءة أو حتى أخذ قيلولة خفيفة.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "التعافي",
                    "desc": "لا يتطلب العلاج أي فترة تعافي، ويمكنك العودة مباشرةً إلى أنشطتك اليومية. قد تلاحظ احمراراً طفيفاً أو تورماً أو خدراً في المنطقة المعالجة، ولكن عادةً ما تزول هذه الأعراض خلال بضعة أيام.",
                    "icon": "fa-solid fa-heart-pulse"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "توفر تقنية تحلل الدهون بالتبريد مزايا عديدة لكل من يبحث عن وسيلة غير جراحية لتقليل الدهون",
            "para": "ويعتبر من أول خيارات العلاج المتاحة عندما يتعلق الأمر بنحت الجسم. وذلك لأنه:",
            "items": [
                {
                    "title": "إجراء غير جراحي",
                    "desc": "لا يتطلب تدخل جراحي، أو استخدام إبر أو التخدير، كما لا يتطلب فترة تعافي بعد العلاج.",
                    "icon": "fa-solid fa-circle-check"
                },
                {
                    "title": "يستهدف الدهون العنيدة",
                    "desc": "تستهدف الدهون العنيدة التي تتراكم في مناطق لا تستجيب للنظام الغذائي أو التمارين الرياضية.",
                    "icon": "fa-solid fa-bullseye"
                },
                {
                    "title": "يوفر نتائج طويلة الأمد",
                    "desc": "لا تعود الخلايا الدهنية المدمرة بالتبريد مجددًا.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "يوفر تعافي سريع",
                    "desc": "يمكن للمريض العودة لممارسة أنشطته اليومية فور انتهاء الجلسة، مما يجعلها خيارًا مثالياً لذوي الجداول المزدحمة.",
                    "icon": "fa-solid fa-business-time"
                },
                {
                    "title": "قد يسبب آثار جانبية طفيفة",
                    "desc": "قد يظهر بعض الاحمرار أو التورم أو الخدر الخفيف، لكنها تختفي عادةً خلال بضعة أيام.",
                    "icon": "fa-solid fa-bandaid"
                },
                {
                    "title": "يلائم استخدامه لعدة مناطق في الجسم",
                    "desc": "تستهدف مناطق مختلفة مثل البطن، والجانبين، والفخذين، والذراعين، وحتى الذقن المزدوجة.",
                    "icon": "fa-solid fa-map-location-dot"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "من هو المرشح المناسب لتقنية تحلل الدهون بالتبريد؟",
            "para": "تُعد تقنية الكرايو ليبوليسيس مثالية للأشخاص الذين يقترب وزنهم من الوزن المثالي، ولكن يعانون من تراكمات دهنية عنيدة في مناطق معينة. مع ذلك، يجب التنويه إلى أنها ليست علاجًا لإنقاص الوزن. لذلك، يُعد معرفة من هو المرشح المثالي لهذا الإجراء أمرًا ضروريًا لتحقيق أفضل النتائج.",
            "items": [
                {
                    "title": "الأفراد الأصحاء",
                    "desc": "يجب أن يكون المرشحون في حالة صحية جيدة ولا يعانون من أمراض طبية خطيرة.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "تراكمات دهنية موضعية",
                    "desc": "يكون العلاج مناسب للأشخاص الذين لديهم دهون زائدة في مناطق مثل البطن، أو الجانبين، أو الذراعين أو الفخذين.",
                    "icon": "fa-solid fa-hand-holding-hand"
                },
                {
                    "title": "الأشخاص الذين وزنهم قريب من الوزن المثالي",
                    "desc": "تعد هذه التقنية مثالية للرجال والنساء الذين لم يتمكنوا من التخلص من جيوب الدهون العنيدة عبر النظام الغذائي أو ممارسة الرياضة.",
                    "icon": "fa-solid fa-weight-scale"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "كيف تستعد للجلسة",
            "para": "رغم أن التحضير لتقنية الكرايو ليبوليسيس بسيط، إلا أنه يمكن أن يساهم اتباع بعض الخطوات في ضمان سلاسة الجلسة وتحقيق أفضل النتائج:",
            "items": [
                {
                    "title": "الاستشارة",
                    "desc": "احجز استشارة مع متخصص ذو خبرة، لتحديد مدى ملاءمة العلاج لك ومناقشة أهدافك.",
                    "icon": "fa-solid fa-clipboard-question"
                },
                {
                    "title": "الحفاظ على ترطيب الجسم",
                    "desc": "احرص على شرب كميات كافية من الماء قبل الجلسة وبعدها، لدعم عملية التخلص من الخلايا الدهنية.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "ارتداء ملابس مريحة",
                    "desc": "اختر ملابس فضفاضة ومريحة خاصة حول المنطقة المستهدفة، لتسهيل الخضوع للعلاج دون إزعاج.",
                    "icon": "fa-solid fa-shirt"
                },
                {
                    "title": "تجنب مضادات الالتهاب",
                    "desc": "امتنع عن تناول الأدوية المضادة للالتهاب مثل الإيبوبروفين لمدة أسبوع قبل الجلسة للحد من احتمالية ظهور الكدمات.",
                    "icon": "fa-solid fa-pills"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "ما يمكن توقعه",
            "para": "تهدف جلسة الكرايو ليبوليسيس إلى تحقيق فقدان تدريجي للدهون بمرور الوقت، ونتائج تدوم مع الالتزام بنظام غذائي صحي ونمط حياة متوازن. يساعد فهم الإطار الزمني للنتائج على تحديد التوقعات بشكل واقعي.",
            "items": [
                {
                    "title": "نتائج مرئية",
                    "desc": "قد تبدأ النتائج بالظهور بعد 3 أسابيع من الجلسة تقريبًا، ولكن يلاحظ غالبية المرضى أفضل تحسن خلال فترة تتراوح بين شهرين إلى ثلاثة أشهر.",
                    "icon": "fa-solid fa-eye"
                },
                {
                    "title": "تحسن مستمر",
                    "desc": "يمكن أن يستمر الجسم في التخلص من الخلايا الدهنية المتضررة حتى 6 أشهر بعد العلاج.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "نتائج تدوم طويلًا",
                    "desc": "نظرًا لأن الخلايا الدهنية يتم تدميرها بشكل نهائي، فإن الحفاظ على القوام الجديد يتطلب التمسك بنظام غذائي متوازن وممارسة الرياضة بانتظام.",
                    "icon": "fa-solid fa-infinity"
                }
            ]
        },
        "aftercareTips": {
            "heading": "نصائح العناية بعد الجلسة",
            "subheading": "للحفاظ على النتائج",
            "para": "لتعظيم النتائج والحفاظ عليها بعد جلسة الكرايو ليبوليسيس، ننصح باتباع هذه الإرشادات:",
            "items": [
                {
                    "title": "الحفاظ على ترطيب الجسم",
                    "desc": "استمر في شرب كميات كافية من الماء للمساعدة في التخلص من الخلايا الدهنية المدمرة.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "اتباع نظام غذائي صحي",
                    "desc": "حافظ على نظام غذائي متوازن لمنع تراكم الدهون الجديدة في المناطق المعالجة.",
                    "icon": "fa-solid fa-apple-whole"
                },
                {
                    "title": "ممارسة الرياضة بانتظام",
                    "desc": "اجمع بين العلاج والنشاط البدني المنتظم لتعزيز النتائج.",
                    "icon": "fa-solid fa-person-walking"
                },
                {
                    "title": "جلسات المتابعة",
                    "desc": "فكر في جلسات إضافية إذا لزم الأمر للحصول على نتائج مثالية.",
                    "icon": "fa-solid fa-calendar-days"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "أسئلة شائعة حول الكرايو ليبوليسيس",
            "items": [
                {
                    "question": "كم عدد الجلسات التي سأحتاجها؟",
                    "answer": "يحقق معظم المرضى النتائج المرجوة بعد 1-2 جلسة لكل منطقة علاجية، مع تباعد بضعة أشهر بينها. سيوصي الممارس بالعدد الأمثل خلال استشارتك."
                },
                {
                    "question": "هل العلاج مؤلم؟",
                    "answer": "قد تشعر ببرودة شديدة وإحساس بالشد في البداية، لكن هذا عادة ما يهدأ مع تخدير المنطقة. معظم المرضى يقرأون أو يسترخون أثناء الإجراء."
                },
                {
                    "question": "متى سأرى النتائج؟",
                    "answer": "قد تبدأ بملاحظة النتائج خلال ثلاثة أسابيع بعد العلاج، مع ظهور النتائج الكاملة بعد شهرين إلى ثلاثة أشهر حيث يتخلص جسمك بشكل طبيعي من الخلايا الدهنية."
                },
                {
                    "question": "هل هناك أي آثار جانبية؟",
                    "answer": "تشمل الآثار الجانبية المؤقتة الشائعة الاحمرار والتورم والكدمات والخدر أو الوخز في المنطقة المعالجة، والتي تختفي عادة في غضون أيام أو أسابيع."
                },
                {
                    "question": "هل تقليل الدهون دائم؟",
                    "answer": "نعم، يتم التخلص من الخلايا الدهنية المدمرة بشكل دائم. ومع ذلك، من المهم الحفاظ على وزن ثابت من خلال النظام الغذائي والتمارين الرياضية لمنع الخلايا الدهنية المتبقية من التضخم."
                },
                {
                    "question": "من ليس مرشحًا جيدًا؟",
                    "answer": "لا يناسب الكرايو ليبوليسيس الأشخاص المصابين بالسمنة أو أولئك الذين يعانون من برودة الدم الانتيابية أو أمراض التجلط البارد أو الهيموغلوبينية الباردة الانتيابية."
                }
            ]
        },
        "treatmentAreas1": [
            {
                "ar": "البطن",
                "title": "البطن",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/abdomen-cryo.jpg"
            },
            {
                "ar": "الجوانب",
                "title": "الجوانب",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/flanks-cryo.jpg"
            },
            {
                "ar": "الفخذين",
                "title": "الفخذين",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/thighs-cryo.jpg"
            }
        ],
        "treatmentAreas2": [
            {
                "ar": "الذراعين",
                "title": "الذراعين",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/arms-cryo.jpg"
            },
            {
                "ar": "الظهر",
                "title": "الظهر",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/back-cryo.jpg"
            },
            {
                "ar": "الذقن المزدوج",
                "title": "الذقن المزدوج",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/chin-cryo.jpg"
            }
        ]
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Cryolipolysis-services-in-dubai-eBH-4.jpg",
            "beforeAfter": [
                {
                    src: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Cryolipolysis-services-in-dubai-eBH-1.jpg",
                    alt: "Cryolipolysis services in dubai eBH (1)",
                },
                {
                    src: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Cryolipolysis-services-in-dubai-eBH-2.jpg",
                    alt: "Cryolipolysis services in dubai eBH (2)",
                },
                {
                    src: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Cryolipolysis-services-in-dubai-eBH-3.jpg",
                    alt: "Cryolipolysis services in dubai eBH (3)",
                },
            ]
        },
        "product": {
            "id": 163,
            "title": "Cryolipolysis (Fat Freezing) in Dubai",
            "description": "Non-invasive fat reduction with cryolipolysis to target and destroy unwanted fat cells for a more sculpted look. Safe, effective treatment with no downtime.",
            "price": 999
        }
    }
}